import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueChart = ({ title, data, type = 'line', height = 300, showControls = true }) => {
  const [chartType, setChartType] = useState(type);
  const [timeRange, setTimeRange] = useState('7d');

  const chartTypes = [
    { id: 'line', label: 'Line', icon: 'TrendingUp' },
    { id: 'area', label: 'Area', icon: 'Activity' },
    { id: 'bar', label: 'Bar', icon: 'BarChart3' }
  ];

  const timeRanges = [
    { id: '24h', label: '24H' },
    { id: '7d', label: '7D' },
    { id: '30d', label: '30D' },
    { id: '90d', label: '90D' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatTooltip = (value, name) => {
    if (name === 'revenue' || name === 'eRPC') {
      return [formatCurrency(value), name?.toUpperCase()];
    }
    return [value, name];
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgb(30, 64, 175)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="rgb(30, 64, 175)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(226, 232, 240)" />
            <XAxis dataKey="date" stroke="rgb(100, 116, 139)" fontSize={12} />
            <YAxis stroke="rgb(100, 116, 139)" fontSize={12} tickFormatter={formatCurrency} />
            <Tooltip formatter={formatTooltip} />
            <Area 
              type="monotone" 
              dataKey="revenue" 
              stroke="rgb(30, 64, 175)" 
              fillOpacity={1} 
              fill="url(#colorRevenue)" 
              strokeWidth={2}
            />
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(226, 232, 240)" />
            <XAxis dataKey="date" stroke="rgb(100, 116, 139)" fontSize={12} />
            <YAxis stroke="rgb(100, 116, 139)" fontSize={12} tickFormatter={formatCurrency} />
            <Tooltip formatter={formatTooltip} />
            <Bar dataKey="revenue" fill="rgb(30, 64, 175)" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(226, 232, 240)" />
            <XAxis dataKey="date" stroke="rgb(100, 116, 139)" fontSize={12} />
            <YAxis stroke="rgb(100, 116, 139)" fontSize={12} tickFormatter={formatCurrency} />
            <Tooltip formatter={formatTooltip} />
            <Line 
              type="monotone" 
              dataKey="revenue" 
              stroke="rgb(30, 64, 175)" 
              strokeWidth={3}
              dot={{ fill: 'rgb(30, 64, 175)', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: 'rgb(30, 64, 175)', strokeWidth: 2 }}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        
        {showControls && (
          <div className="flex items-center space-x-2">
            {/* Time Range Selector */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              {timeRanges?.map((range) => (
                <button
                  key={range?.id}
                  onClick={() => setTimeRange(range?.id)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-smooth ${
                    timeRange === range?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {range?.label}
                </button>
              ))}
            </div>

            {/* Chart Type Selector */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              {chartTypes?.map((chart) => (
                <button
                  key={chart?.id}
                  onClick={() => setChartType(chart?.id)}
                  className={`p-2 rounded transition-smooth ${
                    chartType === chart?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={chart?.label}
                >
                  <Icon name={chart?.icon} size={16} />
                </button>
              ))}
            </div>

            <Button variant="outline" size="sm" iconName="Download">
              Export
            </Button>
          </div>
        )}
      </div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueChart;